import React, { useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, CircleMarker, Marker, Popup, GeoJSON, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { DEFAULT_ROUTE_PATH_OPTIONS } from '../../utils/mapArcs';
import { PATH_TEARDROP, PATH_LODGING, COLOR_IDEA, COLOR_LODGING } from '../../utils/mapMarkerPaths';
import { buildGoogleMapsUrlForMarker } from '../../utils/formatters';

const LODGING_ICON = L.divIcon({
  className: 'tp-map-lodging-marker',
  html: `<svg viewBox="-7 -3 14 6" xmlns="http://www.w3.org/2000/svg" style="width:26px;height:14px">
    <path d="${PATH_LODGING}" fill="${COLOR_LODGING}" stroke="rgba(255,255,255,0.9)" stroke-width="0.5"/>
  </svg>`,
  iconSize: [26, 14],
  iconAnchor: [13, 7],
});

const IDEA_TEARDROP_ICON = L.divIcon({
  className: 'tp-map-idea-teardrop-marker',
  html: `<svg viewBox="-5 -10 10 10" xmlns="http://www.w3.org/2000/svg" style="width:24px;height:24px">
    <path d="${PATH_TEARDROP}" fill="${COLOR_IDEA}" stroke="rgba(255,255,255,0.9)" stroke-width="0.5"/>
  </svg>`,
  iconSize: [24, 24],
  iconAnchor: [12, 24],
});

function FitBoundsButton({ points, countryCode, fitMaxZoom = 5 }) {
  const map = useMap();

  const handleCenter = useCallback(() => {
    const list = Array.isArray(points) ? points : [];
    if (!list.length) return;
    if (list.length === 1) {
      map.setView(L.latLng(list[0][0], list[0][1]), fitMaxZoom, { animate: true });
      return;
    }
    const bounds = L.latLngBounds(list.map((p) => L.latLng(p[0], p[1])));
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [40, 40], maxZoom: fitMaxZoom, animate: true });
  }, [map, points, fitMaxZoom]);

  if (!points?.length) return null;
  return (
    <button
      type="button"
      className="tp-map-center-btn"
      onClick={handleCenter}
      title="Center on route"
      aria-label="Center map on route"
    >
      ⊞
    </button>
  );
}

function FitBoundsToPoints({ points, fitKey, countryCode, fitMaxZoom = 5 }) {
  const map = useMap();

  useEffect(() => {
    const list = Array.isArray(points) ? points : [];
    if (!list.length) return;

    if (list.length === 1) {
      const p = list[0];
      const maxZ = fitMaxZoom ?? 5;
      if (maxZ > 6) {
        map.setView(L.latLng(p[0], p[1]), maxZ, { animate: false });
        return;
      }
      const cc = String(countryCode || '').toUpperCase().trim();
      if (cc) {
        import('../../utils/countriesGeoJson').then(({ getCountriesGeoJson }) => {
          getCountriesGeoJson().then((geo) => {
            try {
              const feats = Array.isArray(geo?.features) ? geo.features : [];
              const match = feats.find((f) => String(f?.properties?.ISO_A2 || '').toUpperCase() === cc);
              if (match && match.geometry) {
                const layer = L.geoJSON(match);
                const bounds = layer.getBounds();
                if (bounds && bounds.isValid && bounds.isValid()) {
                  map.fitBounds(bounds, { padding: [28, 28], maxZoom: 6, animate: false });
                  return;
                }
              }
            } catch {
              // ignore
            }
            map.setView(L.latLng(p[0], p[1]), maxZ, { animate: false });
          });
        });
        return;
      }
      map.setView(L.latLng(p[0], p[1]), Math.min(map.getZoom() || 3, maxZ), { animate: false });
      return;
    }

    const bounds = L.latLngBounds(list.map((p) => L.latLng(p[0], p[1])));
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: fitMaxZoom ?? 5, animate: false });
    }
  }, [map, points, fitKey, countryCode, fitMaxZoom]);

  return null;
}

export default function LeafletMapView({
  center = [20, 0],
  zoom = 3,
  bounds,
  fitPoints = [],
  fitKey = 'default',
  fitMaxZoom = 5,
  countryCode,
  markers = [],
  polylines = [],
  geoJsonOverlay,
  geoJsonStyle,
  theme = 'dark',
  height = 420,
  style = {},
}) {
  const isDark = theme === 'dark';

  const baseUrl = isDark
    ? 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}'
    : 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}';
  const labelsUrl = isDark
    ? 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}'
    : 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}';

  const effectiveCenter = bounds && bounds.length === 2 ? bounds[0] : center;
  const effectiveZoom = zoom;

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
    <MapContainer
      center={effectiveCenter}
      zoom={effectiveZoom}
      bounds={bounds && bounds.length >= 2 ? bounds : undefined}
      style={{ height: '100%', width: '100%', ...style }}
      scrollWheelZoom
    >
      <FitBoundsToPoints points={fitPoints} fitKey={fitKey} countryCode={countryCode} fitMaxZoom={fitMaxZoom} />
      <TileLayer key="esri-base" attribution="Tiles © Esri" url={baseUrl} zIndex={1} />
      <TileLayer key="esri-labels" attribution="Labels © Esri" url={labelsUrl} opacity={0.95} zIndex={2} />

      {geoJsonOverlay && (
        <GeoJSON
          data={geoJsonOverlay}
          interactive={false}
          style={typeof geoJsonStyle === 'function' ? geoJsonStyle : undefined}
        />
      )}

      {polylines.map((pl, idx) =>
        pl.positions && pl.positions.length > 0 ? (
          <Polyline key={`poly-${idx}`} positions={pl.positions} pathOptions={pl.pathOptions || DEFAULT_ROUTE_PATH_OPTIONS} />
        ) : null
      )}

      {markers.map((m, idx) => {
        const lat = Number(m.lat);
        const lon = Number(m.lon);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
        const isLodging = m.kind === 'lodging';
        const isIdea = m.kind === 'idea';
        const mapsUrl = buildGoogleMapsUrlForMarker({ lat, lon, address: m.popup, title: m.title });
        const popupContent = (
          <Popup>
            <div style={{ fontSize: 13 }}>
              {m.title && <div style={{ fontWeight: 700 }}>{m.title}</div>}
              {m.popup && <div style={{ color: 'var(--tp-muted)', marginTop: 4 }}>{m.popup}</div>}
              {mapsUrl && (
                <a href={mapsUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', marginTop: 8, color: 'var(--tp-link, #1a73e8)', fontSize: 13 }}>
                  View in Google Maps →
                </a>
              )}
            </div>
          </Popup>
        );
        if (isLodging) {
          return (
            <Marker key={`marker-${idx}`} position={[lat, lon]} icon={LODGING_ICON}>
              {popupContent}
            </Marker>
          );
        }
        if (isIdea) {
          return (
            <Marker key={`marker-${idx}`} position={[lat, lon]} icon={IDEA_TEARDROP_ICON}>
              {popupContent}
            </Marker>
          );
        }
        // Category Lucide marker (0017) when provided.
        if (m.iconUrl) {
          return (
            <Marker
              key={`marker-${idx}`}
              position={[lat, lon]}
              icon={L.icon({ iconUrl: m.iconUrl, iconSize: [30, 30], iconAnchor: [15, 15], popupAnchor: [0, -14] })}
            >
              {popupContent}
            </Marker>
          );
        }
        return (
          <CircleMarker
            key={`marker-${idx}`}
            center={[lat, lon]}
            radius={m.radius ?? 4}
            pathOptions={m.pathOptions || { color: COLOR_IDEA, fillColor: COLOR_IDEA, fillOpacity: 0.9 }}
          >
            {popupContent}
          </CircleMarker>
        );
      })}
      <FitBoundsButton points={fitPoints} countryCode={countryCode} fitMaxZoom={fitMaxZoom} />
    </MapContainer>
    </div>
  );
}
